/* eslint valid-jsdoc: 0 */
import _ from 'lodash';

/**
 * Common filters that can be used throughout the application
 */
export default angular.module('eventix.common.filters', [])
    .filter('escapeForUrl', function() {
        return function(input) {
            if (input && input.length)
                return encodeURIComponent(input);
            return input;
        };
    })
    .filter('encodeURIComponent', function() {
        return function(input) {
            if (angular.isString(input))
                return encodeURIComponent(input);
            return input;
        };
    })
    .filter('max', function() {
        return function(input, ...args) {
            return Math.max(input, ...args);
        };
    })
    .filter('min', function() {
        return function(input, ...args) {
            return Math.min(input, ...args);
        };
    })
    .filter('hasNestedErrors', function() {
        return function(errorObj) {
            return !(_.isEmpty(errorObj) || flatten(errorObj).length < 1);
        };

        function flatten(obj) {
            if (_.isArray(obj)) {
                if (_.isArray(_.first(obj)))
                    return flatten(_.flatten(obj));
                if (_.isObject(_.first(obj)))
                    return flatten(_.map(obj, o => _.values(o)));
                return obj;
            } else if (_.isObject(obj)) {
                obj = _.values(obj);
                if (_.isObject(_.first(obj)))
                    return flatten(obj);
                return obj;
            }
            return obj;
        }
    })
    .filter('fallback', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:fallback
         * @description
         * Filter an array based on two timestamps
         *
         * @param {Any} input A (potentially) false-y value
         * @param {Any} fallback A fallback value
         * @returns {Any} The input or fallback
         */
        return function(input, fallback) {
            if (!input) return fallback;
            return input;
        };
    })
    .filter('dateRange', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:dateRange
         * @description
         * Filter an array based on two timestamps
         *
         * @param {Object[]} objects Array to filter
         * @param {String} dateKey The attribute name containing a date on `objects`, nested property supported
         * @param {Date|String} from The lower filter bound
         * @param {Date|String} until The upper filter bound
         * @param {String} dateFormat Date format string (see momentjs) for parsing dates
         * @returns {Object[]} A filtered array
         */
        return function(objects, dateKey, from, until, dateFormat = 'YYYY-MM-DDTHH:mm:ss+ZZ') {
            if (!_.isArray(objects) || !objects.length)
                return objects;
            from = typeof from === 'string' ? moment(from, dateFormat) : moment(from);
            until = typeof until === 'string' ? moment(until, dateFormat) : moment(until);
            from.startOf('day');
            until.endOf('day');
            return _.filter(objects, object => {
                let value = _.get(object, dateKey);
                let compare = typeof value === 'string' ?
                    moment(value, dateFormat) : moment(value);
                return compare.isAfter(from) && compare.isBefore(until);
            });
        };
    })
    .filter('whereIn', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:whereIn
         * @description
         * Filter an array based on a selection of values
         *
         * @param {Object[]} objects Array to filter
         * @param {String} property The attribute name containing the property to filter, nested property supported
         * @param {Any[]} values The values that are whitelisted
         * @returns {Object[]} A filtered array
         */
        return function(objects, property, values) {
            if (!objects.length)
                return objects;
            return _.filter(objects, object => {
                let value = _.get(object, property);
                return _.find(values, value) !== undefined;
            });
        };
    })
    .filter('toHtml', function($sce) {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:toHtml
         * @description
         * Run a piece of trusted HTML through `$sce.trustAsHtml` so that it can be bound to dom using `ng-bind-html`
         *
         * @param {String} input Valid and safe HTML
         * @returns {Object} A safe object for `ng-bind-html`
         */
        return function(input) {
            if (!_.isString(input))
                return input;
            return $sce.trustAsHtml(input);
        };
    })
    .filter('min', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:min
         * @description
         * Runs input through Math.min
         * @param {Number|Number[]} Another number to compare
         */
        return function(input, ...rest) {
            if (!_.isNumber(input))
                return input;
            if (rest.length === 1 && _.isArray(rest[0]))
                rest = rest[0];
            return Math.min(input, ...rest);
        };
    })
    .filter('max', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:max
         * @description
         * Runs input through Math.max
         * @param {Number|Number[]} Another number to compare
         */
        return function(input, ...rest) {
            if (!_.isNumber(input))
                return input;
            if (rest.length === 1 && _.isArray(rest[0]))
                rest = rest[0];
            return Math.max(input, ...rest);
        };
    })
    .filter('percentage', function($sce, $filter) {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:percentage
         * @description
         * Format a number as a percentage
         *
         * @example
         * <pre>
         * <div>{{ vm.price |  percentage:2 }}</div>
         * </pre>
         *
         * @param {String} price The price to format
         * @param {String} [decimals] How many decimals
         * @return {String} A formatted percentage
         */
        return function(input, decimals) {
            if (isNaN(+input))
                return input;
            return $filter('number')(input * 100, decimals) + '%';
        };
    })
    .filter('formatCurrency', function(Locales, Currencies, $sce) {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:formatCurrency
         * @requires eventix.common.services.Locales
         * @description
         * Format a number as a currency
         *
         * Converts the database value (in smallest denominator) to a regular value,
         * formats a number with the right seperators and currency symbol.
         *
         * Relies on the `Intl` JavaScript module. Since this is relatively new, ensure
         * a polyfill is available.
         *
         * @example
         * <pre>
         * <div>{{ vm.price |  formatCurrency:'EUR':'nl_NL' }}</div>
         * </pre>
         *
         * @param {String} price The price to format
         * @param {String} [currency] The currency code (EUR, USD)
         * @param {String} [locale] The locale code that will determine the seperator symbols, defaults
         * to user's selected locale
         * @return {String} A formatted price
         */
        return function(price, currency, locale) {
            if (isNaN(+price))
                return price;
            if (!currency) currency = Currencies.selected;
            if (!locale) locale = Locales.selected || Locales[0].code;
            /* eslint-disable */
            var formatter = Intl.NumberFormat(locale.replace('_', '-'), {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            /* eslint-enable */
            switch (currency) {
                default:
                    price = price / 100;
                    break;
            }

            // If the price =~ 0 then Intl.NumberFormat has the unfortunate
            // tendency to format it as €0,00- (minus), so we make it
            // insignificantly positive to prevent this.
            if (price < 0.01 && price > -0.01)
                return '-';
            return formatter.format(price);
        };
    })
    .filter('localNumber', function(Locales) {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:localNumber
         * @requires eventix.common.services.Locales
         * @description
         * Format a number in the user's locale
         *
         * Relies on the `Intl` JavaScript module. Since this is relatively new, ensure
         * a polyfill is available.
         *
         * @example
         * <pre>
         * <div>{{ vm.price |  localNumber:'nl_NL' }}</div>
         * </pre>
         *
         * @param {Number} number The number to format
         * @param {String} [locale] The locale code that will determine the seperator symbols, defaults
         * to user's selected locale
         * @return {String} A formatted price
         */
        return function(number, locale) {
            /* eslint-disable */
            var formatter = Intl.NumberFormat((locale || Locales.selected).replace('_', '-'));
            /* eslint-enable */
            return formatter.format(number);
        };
    })
    .filter('belongsTo', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:belongsTo
         * @description
         * Lookup parent relation for model
         *
         * @example
         * <pre>
         * <div>{{ child.parent_id |  belongsTo:vm.parents:'guid' }}</div>
         * </pre>
         *
         * @param {Any} id The id of the parent model
         * @param {Object[]} models The potential candidates for parent
         * @param {Any} [foreignKey="guid"] The name of the parent property that should match the `id`
         * @return {Object} An item from models or undefined
         */
        return function(id, models, foreignKey = 'guid') {
            var where = {};
            where[foreignKey] = id;
            return _.find(models, where);
        };
    })
    .filter('hasMany', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:hasMany
         * @description
         * Lookup child relations for model
         *
         * @example
         * <pre>
         * {{ parent.id | hasMany:vm.children:'parent_id' }}
         * </pre>
         *
         * @param {Any} id The id of the parent model
         * @param {Object[]} models The potential candidates for children
         * @param {Any} foreignKey The name of the child property that should match the `id`
         * @return {Object[]} An item from models or undefined
         */
        return function(id, models, foreignKey) {
            var where = {};
            where[foreignKey] = id;
            return _.filter(models, where);
        };
    })
    .filter('lodash', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:lodash
         * @description
         * A proxy for lodash functionality
         *
         * WARNING: this is (potentially) a computationally expensive filter
         *
         * @example
         * <pre>
         * {{ vm.collectionOfObjects | lodash:'pick':'attribute' }}
         * </pre>
         *
         * @param {Any} input The data that Lodash should operate on
         * @param {String} fn The name of the Lodash function
         * @param {Any} args Any additional parameters will be passed to _[fn](input, args...)
         * @return {Any} The result from Lodash
         */
        return function(input, fn, ...args) {
            args.unshift(input);
            return _[fn].apply(null, args);
        };
    })
    .filter('maxChars', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:maxChars
         * @description
         * Limit a string to `max` characters
         *
         * @param {String} input Text to limit
         * @param {Number} [length=50] Maximal length
         * @param {String} [ellipsis='…'] Cut off character (defaults to 3 ellipsis)
         * @return {String} The resulting text
         */
        return function(input, length = 50, ellipsis = '…') {
            if (angular.isString(input) && input.length > length)
                return input.slice(0, length) + ellipsis;
            return input;
        };
    })
    .filter('startFrom', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:startFrom
         * @description
         * The opposite of `limitTo`, slices an array from `start`
         *
         * @param {Array} input Array of items
         * @param {Number} start Starting index
         * @return {Array} The resulting array
         */
        return function(input, start) {
            if (input) {
                start = Math.abs(start);
                return input.slice(start);
            }
            return [];
        };
    })
    .filter('capitalize', function() {
        /**
         * Capitalize the first letter
         *
         * @param {String} input Text
         * @return {string} Capitalized
         */
        return function(input) {
            if (typeof input !== 'string') return input;
            return input.charAt(0).toUpperCase() + input.slice(1);
        };
    })
    .filter('humanize', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:humanize
         * @description
         * Parse a variable name to something humanly legible
         *
         * @example
         * <pre>
         * {{ 'someVar'  | humanize }} => Some var
         * {{ 'bag_item' | humanize }} => Bag item
         * </pre>
         *
         * @param {String} input String to humanize
         * @return {String} Humanized string
         */
        return function(input) {
            if (typeof input !== 'string') return input;
            input = input.replace(/[_-]/g, ' ');
            input = input.replace(/\./g, ' > ');
            input = input.replace(/[A-Z]/g, function(cap) {
                return ' ' + cap.toLowerCase();
            }).trim();
            return input.charAt(0).toUpperCase() + input.slice(1);
        };
    })
    .filter('debug', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:debug
         * @description
         * Pretty print a variable as JSON
         *
         * @param {any} input Any serializable input
         * @returns {String} JSON stringified input
         */
        return function(input, format = 4) {
            try {
                return JSON.stringify(input, null, format);
            } catch (e) {
                return input + '';
            }
        };
    })
    .filter('includes', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:includes
         * @description
         * Check if string or arrays includes a value
         *
         * @param {String|Array} haystack Your haystack
         * @param {Any} [needle] The needle to search for
         * @return {Boolean} True if it was found, undefined if not
         */
        return function(haystack, needle) {
            return _.includes(haystack, needle) || undefined;
        };
    })
    .filter('toLocaltime', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:toLocaltime
         * @description
         * Run a timestamp through moment and convert it to your local timezone
         *
         * @param {any} input An ISO8601 date/time string YYYY-MM-DDTHH:mm:ss+ZZ
         * @param {string} [dstFormat='YYYY-MM-DD HH:mm'] Optional format string for the output (see [momentjs.com](momentjs.com) for options)
         * @return {string} Date converted to local time
         */
        return function(input, dstFormat = 'YYYY-MM-DD HH:mm') {
            return moment(input, 'YYYY-MM-DDTHH:mm:ssZ').format(dstFormat);
        };
    })
    .filter('moment', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:moment
         * @description
         * Run through MomentJS formatting
         *
         * @example
         * <pre>
         * {{ vm.unixTimestamp | moment:'DD-MM-YYYY':'x' }}
         * </pre>
         *
         * @param {Any} input the date/string/number representing a date
         * @param {String} dstFormat Format string for the output (see [momentjs.com](momentjs.com) for options
         * @param {String} [srcFormat='YYYY-MM-DDTHH:mm:ssZ'] Optional format string of the input (see [momentjs.com](momentjs.com) for options)
         * @return {String} Reformatted date or null if invalid date
         */
        return function(input, dstFormat, srcFormat = 'YYYY-MM-DDTHH:mm:ssZ') {
            var momentObject = moment(input, srcFormat);
            return momentObject.isValid() ? momentObject.format(dstFormat) : null;
        };
    })
    .filter('momentFromNow', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:momentFromNow
         * @description
         * Run through MomentJS formatting for human readable difference with now.
         *
         * @example
         * <pre>
         * {{ vm.unixTimestamp | momentFromNow:'x' }}
         * </pre>
         *
         * @param {Any} input the date/string/number representing a date
         * @param {String} [srcFormat='YYYY-MM-DDTHH:mm:ssZ'] Optional format string of the input (see [momentjs.com](momentjs.com) for options)
         * @return {String} Reformatted (From Now) date or null if invalid date
         */
        return function(input, srcFormat = 'YYYY-MM-DDTHH:mm:ssZ') {
            var momentObject = moment(input, srcFormat);
            var returning = momentObject.isValid() ? momentObject.fromNow() : null;

            return returning;
        };
    })
    .filter('inCollapse', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:inCollapse
         * @description
         * Filter an array, allowing only tickets that are inside the collapse
         * @param {Array<Ticket>} tickets Array of tickets
         * @param {Collapse} collapse A collapse
         * @returns {Array<Ticket>} A filtered list
         */
        return function(tickets, collapse) {
            return _.filter(tickets, t => collapse.hasTicket(t));
        };
    })
    .filter('notInCollapses', function() {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:notInCollapse
         * @description
         * Filter an array, allowing only tickets that are **not** inside the collapses
         * @param {Array<Ticket>} tickets Array of tickets
         * @param {Array<Collapse>} collapses Array of collapses
         * @returns {Array<Ticket>} A filtered list
         */
        return function(tickets, collapses) {
            let inCollapses = _.flatMap(collapses, 'ticket_guids');
            return _.filter(tickets, ticket => {
                return !_.includes(inCollapses, ticket.guid);
            });
        };
    })
    .filter('format', function($filter) {
        /**
         * @ngdoc filter
         * @name eventix.common.filters:format
         * @description
         * Run through different filters based on string
         *
         * @example
         * <pre>
         * {{ vm.currencyValue | format:'currency' }}
         * {{ vm.numberOrStringValue | format:'none' }}
         * </pre>
         *
         * @param {Any} input the date/string/number to be formatted
         * @param {String} dstFormat String describing format (ex. 'currency')
         * @return {String} Reformatted string
         */
        return function(input, dstFormat, ...options) {
            if (dstFormat === 'currency' && !isNaN(+input))
                return $filter('formatCurrency')(input);
            if (dstFormat === 'date' && input.length > 19)
                return $filter('moment')(input, ...options);
            return input;
        };
    }).name;
